import { SkeletonLoader } from '@freelancelabs/components';

/**
 * Loading skeletons, shared. They live here and not in the design system because they add
 * no style: they assemble `SkeletonLoader` with layout utilities, which the repo rule
 * allows at the application level.
 */

interface ListProps {
    /** How many rows to draw. Set to what the list usually renders. */
    rows?: number;
    /** Draw a checkbox on the left of each row, for a list that is checkable. */
    withControl?: boolean;
    className?: string;
}

export const ListSkeleton = ({ rows = 4, withControl = false, className }: ListProps) => (
    <ul className={`flex flex-col ${className ?? ''}`} aria-busy="true">
        {Array.from({ length: rows }, (_, rang) => (
            <li
                className="flex items-center gap-2.5 border-b border-border-norm px-2.5 py-2 last:border-b-0"
                key={rang}
            >
                {withControl && <SkeletonLoader width={16} height={16} />}
                {/* Widths alternate by rank (not at random, which would jitter on re-render)
                    so a list of names does not read as a table of equal bars. */}
                <SkeletonLoader className={rang % 3 === 0 ? 'w-1/2' : rang % 3 === 1 ? 'w-2/3' : 'w-2/5'} height={14} />
                <SkeletonLoader className="ml-auto" width={56} height={12} />
            </li>
        ))}
    </ul>
);

interface MatrixProps {
    /** How many permission groups to draw. */
    groups?: number;
    /** How many permissions per group. */
    rowsPerGroup?: number;
}

/**
 * The permissions matrix, grouped by module. The group count matches the referential's
 * seven, so the card does not jump when the response arrives.
 */
export const MatrixSkeleton = ({ groups = 7, rowsPerGroup = 3 }: MatrixProps) => (
    <div className="flex flex-col gap-2" aria-busy="true">
        {Array.from({ length: groups }, (_, groupe) => (
            <div className="rounded-md border border-border-norm" key={groupe}>
                <div className="flex items-center gap-2.5 border-b border-border-norm bg-background-weak px-3 py-2.5">
                    <SkeletonLoader className="w-1/4" height={14} />
                    <SkeletonLoader className="ml-auto" width={40} height={12} />
                </div>
                <ul className="flex flex-col px-3 py-1.5">
                    {Array.from({ length: rowsPerGroup }, (_, ligne) => (
                        <li className="flex items-center gap-2.5 py-1.5" key={ligne}>
                            <SkeletonLoader width={16} height={16} />
                            <SkeletonLoader className={ligne % 2 === 0 ? 'w-2/5' : 'w-1/2'} height={12} />
                        </li>
                    ))}
                </ul>
            </div>
        ))}
    </div>
);
